import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  createStockItemQuery,
  listStockItemsQuery,
  postStockMovementQuery,
  stockLedgerQuery
} from "../../../Graphql/querries/education/stockItemQuery";
import {
  CreateStockItemInput,
  CreateStockItemResponse,
  ListStockItemsResponse,
  PostStockMovementInput,
  StockItem,
  StockLedgerResponse
} from "../../../components/Education/StockUsage/types/stockItem";
import {
  GraphQLCodePayload,
  GraphQLData,
  unwrapGraphQLOperation
} from "./educationGraphQLUtils";

export const useStockItems = (input?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["stockItems", input?.search ?? "", input?.page ?? 0],
    queryFn: async (): Promise<StockItem[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListStockItemsResponse, "listStockItems">,
        { input: object }
      >(listStockItemsQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listStockItems");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.stockItems ?? [];
    }
  });
};

export const useCreateStockItem = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateStockItemInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateStockItemResponse, "createStockItem">,
        { input: CreateStockItemInput }
      >(createStockItemQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createStockItem");
      if (response.code !== API_RESPONSE_CODE.OK || !response.id) {
        throw new Error(response.code);
      }
      return response.id;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const usePostStockMovement = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: PostStockMovementInput) => {
      const data = await fetchGraphQL<
        GraphQLData<GraphQLCodePayload, "postStockMovement">,
        { input: PostStockMovementInput }
      >(postStockMovementQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "postStockMovement");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useStockLedger = (stockItemId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["stockLedger", stockItemId ?? ""],
    enabled: Boolean(stockItemId),
    queryFn: async () => {
      const data = await fetchGraphQL<
        GraphQLData<StockLedgerResponse, "stockLedger">,
        { input: { stockItemId?: string } }
      >(stockLedgerQuery, { input: { stockItemId } }, setAlert);
      const response = unwrapGraphQLOperation(data, "stockLedger");
      if (response.code !== API_RESPONSE_CODE.OK) {
        return [];
      }
      return response.movements ?? [];
    }
  });
};
